import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import ListItem from '@material-ui/core/ListItem';
import List from '@material-ui/core/List';
import ListItemText from '@material-ui/core/ListItemText';
import Grid from '@material-ui/core/Grid';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import isFunction from 'lodash/isFunction';
import InformationTag from '@configuratorware/configurator-frontendgui/App/Shared/Components/InformationTag/InformationTag';
import { translationExists, t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import clsx from 'clsx';
import { Divider } from '@material-ui/core';

const styles = (theme) => ({
  MuiListRoot: {
    paddingTop: 0,
    paddingRight: 0,
    paddingLeft: 0,
  },
  boldText: {
    fontWeight: 'bold',
    '& span': {
      fontWeight: 'bold',
    },
  },
  MuiCheckboxRoot: {
    padding: 0,
    [theme.breakpoints.down('xs')]: {
      margin: '0',
    },
  },
  MuiTypographyRoot: {
    lineHeight: 1.8,
  },
  AmountPriceTypographyVat: {
    fontSize: 9,
    lineHeight: 1.2,
    textAlign: 'end',
    display: 'block',
    padding: '0 16px',

    [theme.breakpoints.down('xs')]: {
      fontSize: 9,
      padding: '0 8px',
    },
  },
  MuiTypographyNoWrap: {
    whiteSpace: 'nowrap',
    minWidth: 50,
    fontSize: 15,
    padding: '0 16px',
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
      padding: '0 5px',
    },
  },
  gutterBottom: {
    marginBottom: 0,
  },
  itemTitle: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  listItemPrimary: {
    padding: '0 2px',
    [theme.breakpoints.down('xs')]: {
      padding: 0,
    },
    '& span': {
      fontSize: 15,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'block',
    },
  },
  selectableLabel: {
    padding: '0 10px',
    [theme.breakpoints.down('xs')]: {
      padding: 0,
    },
  },
  infoBtn: {
    flex: '100%',
    [theme.breakpoints.down('xs')]: {
      flex: 0,
      justifyContent: 'flex-end',
      display: 'flex',
    },
  },
  container: {
    marginBottom: 'auto',
  },
});

class PriceList extends React.Component {
  static propTypes = {
    list: PropTypes.array,
    productTitle: PropTypes.string,
    classes: PropTypes.object,
    amount: PropTypes.number,
    totalPrice: PropTypes.string,
    showTotalPrice: PropTypes.bool,
    shippingPrice: PropTypes.string,
    taxNote: PropTypes.string,
    hidePrices: PropTypes.bool,
    showReceiveOfferForm: PropTypes.bool,
    onSelectableChanged: PropTypes.func,
    vatNotice: PropTypes.string,
  };

  constructor(props) {
    super(props);
  }

  handleToggle = (element) => () => {
    if (isFunction(this.props.onSelectableChanged)) {
      this.props.onSelectableChanged(element.key, !element.selected);
    }
  };

  render() {
    const {
      list,
      productTitle,
      classes,
      amount,
      hidePrices,
      shippingPrice,
      totalPrice,
      showTotalPrice = false,
      vatNotice = null,
    } = this.props;
    return (
      <Grid container spacing={2} classes={{ root: classes.container }}>
        <Grid item xs={12}>
          {amount > 0 && list && list.length <= 0 && (
            <List dense={false}>
              <ListItem classes={{ root: classes.MuiListRoot }}>
                <Grid container alignItems="center">
                  <Grid item xs={4}>
                    <Typography
                      variant="body2"
                      gutterBottom
                      classes={{
                        root: classes.MuiTypographyNoWrap,
                        gutterBottom: classes.gutterBottom,
                      }}
                    >
                      {amount} {t('amountPrice.pieces')}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <ListItemText className={classes.listItemPrimary} primary={productTitle} />
                  </Grid>
                </Grid>
              </ListItem>
            </List>
          )}
          {list && list.length > 0 && (
            <List dense={false}>
              {list.map((element) => {
                const hasProductionInfoIcon =
                  element.key === 'production_cost' && translationExists('productionCostInformation');
                const hasSetupInfoIcon =
                  element.key === 'setup_cost' && translationExists('setUpCostInformation');

                return (
                  <ListItem key={element.key} classes={{ root: classes.MuiListRoot }}>
                    <Grid container alignItems="center">
                      {element.selectable && (
                        <Grid item xs={1}>
                          <Checkbox
                            checked={element.selected}
                            color="primary"
                            tabIndex={-1}
                            disableRipple
                            onChange={this.handleToggle(element)}
                            classes={{ root: classes.MuiCheckboxRoot }}
                          />
                        </Grid>
                      )}
                      <Grid item xs={4}>
                        <ListItemText
                          className={clsx(
                            list[0].key === element.key && classes.itemTitle,
                            list[0].key !== element.key && classes.listItemPrimary,
                            element.selectable && classes.selectableLabel
                          )}
                          primary={element.label}
                        />
                      </Grid>
                      {hasSetupInfoIcon && (
                        <Grid item xs={1} className={classes.infoBtn}>
                          <InformationTag infoText={t('setUpCostInformation')} />
                        </Grid>
                      )}
                      {hasProductionInfoIcon && (
                        <Grid item xs={1} className={classes.infoBtn}>
                          <InformationTag infoText={t('productionCostInformation')} />
                        </Grid>
                      )}
                      <Grid item xs={2}>
                        <Typography
                          align="right"
                          classes={{
                            root: classes.MuiTypographyNoWrap,
                            gutterBottom: classes.gutterBottom,
                          }}
                        >
                          {`${element.quantity}x`}
                        </Typography>
                      </Grid>
                      {!hidePrices && (
                        <Grid item xs={hasProductionInfoIcon || hasSetupInfoIcon ? 2 : 3}>
                          <Typography
                            align="right"
                            classes={{
                              root: classes.MuiTypographyNoWrap,
                              gutterBottom: classes.gutterBottom,
                            }}
                          >
                            {element.unitPrice}
                          </Typography>
                        </Grid>
                      )}
                      {!hidePrices && (
                        <Grid item xs={hasProductionInfoIcon || hasSetupInfoIcon ? 2 : 3}>
                          <Typography
                            align="right"
                            classes={{
                              root: classes.MuiTypographyNoWrap,
                              gutterBottom: classes.gutterBottom,
                            }}
                          >
                            {element.selectable && !element.selected ? '' : element.price}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </ListItem>
                );
              })}
            </List>
          )}
          {shippingPrice && (
            <>
              <Divider />
              <List dense={false}>
                <ListItem classes={{ root: classes.MuiListRoot }} style={{ padding: '20px 0px' }}>
                  <Grid container alignItems="center">
                    <Grid item xs={4}>
                      <ListItemText className={classes.itemTitle} primary={t('amountPrice.shippingCosts')} />
                    </Grid>
                    <Grid item xs={8}>
                      <Typography
                        align="right"
                        classes={{
                          root: classes.MuiTypographyNoWrap,
                          gutterBottom: classes.gutterBottom,
                        }}
                      >
                        {shippingPrice}
                      </Typography>
                    </Grid>
                  </Grid>
                </ListItem>
              </List>
            </>
          )}
          {showTotalPrice && totalPrice && (
            <>
              <Divider />
              <List dense={false}>
                <ListItem classes={{ root: classes.MuiListRoot }}>
                  <Grid container alignItems="center">
                    <Grid item xs={4}>
                      <ListItemText
                        className={clsx(classes.itemTitle, classes.boldText)}
                        primary={t('amountPrice.totalPrice')}
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <Typography
                        align="right"
                        classes={{
                          root: clsx(classes.MuiTypographyNoWrap, classes.boldText),
                          gutterBottom: classes.gutterBottom,
                        }}
                      >
                        {totalPrice}
                      </Typography>
                      {vatNotice && (
                        <Typography
                          variant="caption"
                          classes={{
                            root: classes.AmountPriceTypographyVat,
                          }}
                        >
                          <i>{vatNotice}</i>
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </ListItem>
              </List>
            </>
          )}
        </Grid>
      </Grid>
    );
  }
}

export default withStyles(styles, { name: 'PriceList' })(PriceList);
